import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { DeliveryRunInspector } from '~/features/deliveries/components/run-inspector/delivery-run-inspector';

interface Props {
  /** The batch's delivery run (`BatchHeader.deliveryJobId`); absent for mock/suppressed runs. */
  runId: string | null | undefined;
  /** The batch — Retry delivery opens its wizard in redeliver mode. */
  batchId: string;
}

/**
 * "Delivery run" view of the Compare Delivery page (admins only): the full
 * Temporal-style `DeliveryRunInspector` for the batch's delivery run. Runs
 * opened from the retry chain (or created by a retry) are followed in place,
 * with a way back — the console route they'd otherwise open is super-admin only.
 */
export function DeliveryCompareRunView({ runId, batchId }: Props) {
  const { t } = useTranslation('catalog-operations');
  const [followedRunId, setFollowedRunId] = useState<string | null>(null);
  const activeRunId = followedRunId ?? runId ?? null;

  if (!activeRunId) {
    return (
      <div
        className="bg-background text-muted-foreground flex h-48 items-center justify-center rounded-xl border p-6 text-center text-sm"
        data-testid="delivery-compare-run-empty"
      >
        {t('deliveryCompare.views.noRun')}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="delivery-compare-run-view">
      {followedRunId && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="cursor-pointer"
          onClick={() => {
            setFollowedRunId(null);
          }}
          data-testid="delivery-compare-run-back-to-batch-run-button"
        >
          {t('deliveryCompare.views.backToBatchRun')}
        </Button>
      )}
      <DeliveryRunInspector
        key={activeRunId}
        runId={activeRunId}
        onRunChange={setFollowedRunId}
        batchId={batchId}
        testId="delivery-compare-run-inspector"
      />
    </div>
  );
}
